import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiHandler } from '../assets/api';
import { endpoint } from '../assets/api/endpoint';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { saveToken, saveUserId, saveUserAuthId, saveLoggedIn } from '../redux/UserDataSlice';

const GoogleSignInButton = ({ 
  buttonText = "Continue with Google",
  showDefaultButton = true,
  customButtonStyle = {},
  onSuccess,
  onError 
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const googleButtonRef = useRef(null);
  
  const WEB_CLIENT_ID = "332654565913-llp0fuljm03it6illsrudfds8c9qcqjo.apps.googleusercontent.com";

  useEffect(() => {
    const loadGoogleScript = () => {
      return new Promise((resolve, reject) => {
        if (window.google) {
          resolve(window.google);
          return;
        }

        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.onload = () => resolve(window.google);
        script.onerror = reject;
        document.head.appendChild(script);
      });
    };

    loadGoogleScript().then(() => {
      initializeGoogleSignIn();
    }).catch((error) => {
      console.error('Failed to load Google script:', error);
      toast.error('Failed to load Google Sign-In');
    });
  }, []);

  const handleCredentialResponse = async (response) => {
    try {
      const idToken = response.credential;
      const apiResponse = await apiHandler({
        url: `${endpoint.GOOGLE_REGISTER}?idToken=${idToken}`,
        method: 'POST',
      });

      if (apiResponse.data.message) {
        if (apiResponse.data.isRegistered) {
          localStorage.setItem("googleLoginData", JSON.stringify({ idToken }));
          localStorage.setItem("authToken", apiResponse.data.token);
          
          const token = apiResponse.data.token;
          dispatch(saveToken({ accessToken: apiResponse?.data?.token }));
                  dispatch(saveUserId(apiResponse?.data?.userId));
                  dispatch(saveUserAuthId(apiResponse?.data?.authId));
                  dispatch(saveLoggedIn(true));

          // Call success callback if provided
          if (onSuccess) {
            onSuccess(apiResponse.data);
          }

          if (apiResponse.data.isOnboarded === true) {
            toast.success('User Logged Successfully!');
            navigate('/home');
          } else {
            if (apiResponse.data.isPreOnboarded) {
              toast.success('User Logged Successfully!');
              navigate('/subscription-plan');
            } else {
              toast.success('User Logged Successfully!');
              navigate('/avatar-name');
            }
          }
        } else {
          navigate("/google-login", { 
            state: { userDetails: apiResponse.data } 
          });
        }
      } else {
        toast.error('Invalid Token. Please wait for 5 minutes and try again.');
        if (onError) {
          onError(new Error('Invalid Token'));
        }
      }
    } catch (error) {
      console.error("Google Sign-In error:", error);
      toast.error('Sign-in failed. Please try again.');
      
      if (onError) {
        onError(error);
      }
    }
  };

  const initializeGoogleSignIn = () => {
    if (window.google) {
      window.google.accounts.id.initialize({
        client_id: WEB_CLIENT_ID,
        callback: handleCredentialResponse,
        auto_select: false,
        cancel_on_tap_outside: true,
      });

      if (showDefaultButton && googleButtonRef.current) {
        window.google.accounts.id.renderButton(
          googleButtonRef.current,
          {
            theme: 'outline',
            size: 'large',
            width: '100%',
            text: 'signin_with',
            shape: 'rectangular',
          }
        );
      }
    }
  };

  const handleCustomGoogleSignIn = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!window.google) {
      toast.error('Google Sign-In not loaded. Please refresh the page.');
      return;
    }

    try {
      // Try One Tap first
      window.google.accounts.id.prompt((notification) => {
        
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          window.google.accounts.id.disableAutoSelect();
          
          // Re-initialize and render a temporary button programmatically
          const tempContainer = document.createElement('div');
          tempContainer.style.visibility = 'hidden';
          tempContainer.style.position = 'absolute';
          document.body.appendChild(tempContainer);
          
          window.google.accounts.id.renderButton(tempContainer, {
            theme: 'outline',
            size: 'large',
            text: 'signin_with',
          });
          
          // Simulate click on the hidden Google button
          setTimeout(() => {
            const googleBtn = tempContainer.querySelector('[role="button"]');
            if (googleBtn) {
              googleBtn.click();
            }
            // Clean up
            setTimeout(() => {
              if (document.body.contains(tempContainer)) {
                document.body.removeChild(tempContainer);
              }
            }, 1000);
          }, 100);
        }
      });
    } catch (error) {
      console.error('Google Sign-In error:', error);
      toast.error('Sign-in failed. Please try again.');
      if (onError) {
        onError(error);
      }
    }
  };

  const defaultCustomButtonStyle = {
     width: '100%',
    height: '48px',
    border: '1px solid #dadce0',
    borderRadius: '8px',
    backgroundColor: '#fff',
    color: '#3c4043',
    fontSize: '14px',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    ...customButtonStyle
  };

  return (
    <div className=' w-100' >
      {showDefaultButton && (
        <div ref={googleButtonRef} style={{ width: '100%', marginBottom: '10px' }}></div>
      )}
      
      {!showDefaultButton && (
        <button
          type="button"
          onClick={handleCustomGoogleSignIn}
          style={defaultCustomButtonStyle}
          onMouseEnter={(e) => e.target.style.backgroundColor = customButtonStyle.backgroundColor || '#f8f9fa'}
          onMouseLeave={(e) => e.target.style.backgroundColor = customButtonStyle.backgroundColor || '#fff'}
        >
          <svg width="18" height="18" viewBox="0 0 18 18" style={{ marginRight: '8px' }}>
            <path fill="#ffffff" d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 0 0 2.38-5.88c0-.57-.05-.66-.15-1.18z"/>
            <path fill="#34A853" d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2.04a4.8 4.8 0 0 1-7.18-2.53H1.83v2.07A8 8 0 0 0 8.98 17z"/>
            <path fill="#FBBC04" d="M4.5 10.49a4.8 4.8 0 0 1 0-3.07V5.35H1.83a8 8 0 0 0 0 7.28z"/>
            <path fill="#EA4335" d="M8.98 4.72c1.16 0 2.23.4 3.06 1.2l2.3-2.3A8 8 0 0 0 1.83 5.35L4.5 7.42a4.77 4.77 0 0 1 4.48-2.7z"/>
          </svg>
          {buttonText}
        </button>
      )}
    </div>
  );
};

export default GoogleSignInButton;